// Shared helpers for tenant opening hours, evaluated in Nairobi time (EAT UTC+3)

function parseTimeToMinutes(str) {
  const m = str.trim().match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  const mer = m[3].toUpperCase();
  if (mer === "PM" && h !== 12) h += 12;
  if (mer === "AM" && h === 12) h = 0;
  return h * 60 + min;
}

function getTodayHoursString(businessHours, day) {
  if (!businessHours) return null;
  if (day === 0) return businessHours.sunday || businessHours.weekdays;
  if (day === 6) return businessHours.saturday || businessHours.weekdays;
  return businessHours.weekdays;
}

// Returns true if the tenant is currently open based on its real business hours.
export function isOpenNow(businessHours) {
  if (!businessHours) return false;

  const now = new Date();
  const nairobi = new Date(now.toLocaleString("en-US", { timeZone: "Africa/Nairobi" }));
  const day = nairobi.getDay();
  const nowMin = nairobi.getHours() * 60 + nairobi.getMinutes();

  const todayStr = getTodayHoursString(businessHours, day);
  if (!todayStr) return false;

  const lower = todayStr.toLowerCase();
  if (lower.includes("24 hour") || lower.includes("24/7")) return true;
  if (lower.includes("closed")) return false;

  const times = todayStr.match(/\d{1,2}:\d{2}\s*(AM|PM)/gi);
  if (!times || times.length < 2) return false;

  const start = parseTimeToMinutes(times[0]);
  const end = parseTimeToMinutes(times[1]);
  if (start === null || end === null) return false;

  if (end <= start) {
    // Spans past midnight
    return nowMin >= start || nowMin < end;
  }
  return nowMin >= start && nowMin < end;
}

// Converts "8:00 AM - 11:00 PM" -> { opens: "08:00", closes: "23:00" } for JSON-LD.
function toSpecTimes(str) {
  if (!str) return null;
  const lower = str.toLowerCase();
  if (lower.includes("24 hour") || lower.includes("24/7")) return { opens: "00:00", closes: "23:59" };
  if (lower.includes("closed")) return null;
  const times = str.match(/\d{1,2}:\d{2}\s*(AM|PM)/gi);
  if (!times || times.length < 2) return null;
  const s = parseTimeToMinutes(times[0]);
  const e = parseTimeToMinutes(times[1]);
  if (s === null || e === null) return null;
  const fmt = (mins) => `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;
  return { opens: fmt(s), closes: fmt(e) };
}

// Builds schema.org openingHoursSpecification array from a tenant's business_hours.
export function buildOpeningHoursSpecification(businessHours) {
  if (!businessHours) return [];
  const spec = [];
  const weekday = toSpecTimes(businessHours.weekdays);
  if (weekday) {
    spec.push({
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: weekday.opens,
      closes: weekday.closes,
    });
  }
  const sat = toSpecTimes(businessHours.saturday);
  if (sat) {
    spec.push({ "@type": "OpeningHoursSpecification", dayOfWeek: "Saturday", opens: sat.opens, closes: sat.closes });
  }
  const sun = toSpecTimes(businessHours.sunday);
  if (sun) {
    spec.push({ "@type": "OpeningHoursSpecification", dayOfWeek: "Sunday", opens: sun.opens, closes: sun.closes });
  }
  return spec;
}
